import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, BarChart3, Layers, ArrowRight, Star } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Featured Project Hero Component
const FeaturedProject = ({ project }) => {
  if (!project) return null;
  
  const metrics = project.metrics || [];
  
  return (
    <section className="featured-section" data-testid="featured-project">
      <div className="featured-container">
        <div className="featured-content">
          <div className="featured-label">
            <Star size={14} />
            <span>Featured Case Study</span>
          </div>
          <span className="featured-category">{project.category}</span>
          <h2 className="featured-title">{project.title}</h2>
          <p className="featured-subtitle">{project.subtitle}</p>
          <p className="featured-description">{project.overview?.substring(0, 200)}...</p>
          
          {metrics.length > 0 && (
            <div className="featured-metrics">
              {metrics.slice(0, 3).map((m, i) => (
                <div key={i} className="featured-metric">
                  <div className="featured-metric-value">{m.value}</div>
                  <div className="featured-metric-label">{m.label}</div>
                </div>
              ))}
            </div>
          )}
          
          <Link to={`/project/${project.slug}`} className="featured-cta" data-testid="featured-cta">
            <span>View Full Case Study</span>
            <ArrowRight size={18} />
          </Link>
        </div>
        
        <div className="featured-image-wrapper">
          <div className="featured-image-accent"></div>
          <img src={project.image} alt={project.title} className="featured-image" />
        </div>
      </div>
    </section>
  );
};

const ProjectCard = ({ project }) => {
  const metrics = project.metrics || [];
  return (
    <Link to={`/project/${project.slug}`} className="project-card-link" data-testid={`project-card-${project.slug}`}>
      <div className="project-card">
        <div style={{ overflow: 'hidden' }}>
          <img src={project.image} alt={project.title} className="project-image" />
        </div>
        <div className="project-content">
          <span className="project-category">{project.category}</span>
          <h3 className="project-title">{project.title}</h3>
          <p className="project-subtitle">{project.subtitle}</p>
          <p className="project-description">{project.description}</p>
          
          {metrics.length > 0 && (
            <div className="project-metrics">
              {metrics.slice(0, 3).map((m, i) => (
                <div key={i} className="metric">
                  <div className="metric-value">{m.value}</div>
                  <div className="metric-label">{m.label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="view-project-cta">
            <span>View Case Study</span>
            <ArrowRight size={16} />
          </div>
        </div>
      </div>
    </Link>
  );
};

const SkillCard = ({ skillCat }) => {
  const icons = { Search, BarChart3, Layers };
  const Icon = icons[skillCat.icon] || Search;
  const skillsList = skillCat.skills || [];
  
  return (
    <div className="skill-card" data-testid={`skill-card-${skillCat.id}`}>
      <div className="skill-icon" style={{ background: skillCat.icon_bg }}>
        <Icon size={24} color="white" />
      </div>
      <h3 className="skill-title">{skillCat.title}</h3>
      <div className="skill-list">
        {skillsList.map((skill, idx) => (
          <span key={idx} className="skill-tag">{skill}</span>
        ))}
      </div>
    </div>
  );
};

const TimelineItem = ({ exp }) => {
  const highlights = exp.highlights || [];
  return (
    <div className="timeline-item" data-testid={`timeline-${exp.id}`}>
      <div className="timeline-dot"></div>
      <div className="timeline-content">
        <span className="timeline-period">{exp.period}</span>
        <h4 className="timeline-role">{exp.role}</h4>
        <p className="timeline-company">{exp.company} • {exp.location}</p>
        <ul className="timeline-highlights">
          {highlights.map((h, idx) => (
            <li key={idx}>{h}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const EducationCard = ({ edu }) => {
  const modules = edu.modules || [];
  return (
    <div className="skill-card" style={{ marginBottom: '20px' }} data-testid={`education-${edu.id}`}>
      <span className="section-label" style={{ marginBottom: '8px', display: 'block' }}>
        {edu.year}
      </span>
      <h4 className="skill-title" style={{ marginBottom: '6px' }}>
        {edu.degree}
      </h4>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', fontSize: '0.875rem' }}>
        {edu.institution} • {edu.location}
      </p>
      <div className="skill-list">
        {modules.slice(0, 4).map((mod, idx) => (
          <span key={idx} className="skill-tag">{mod}</span>
        ))}
      </div>
    </div>
  );
};

const UXPortfolio = () => {
  const [projects, setProjects] = useState([]);
  const [experience, setExperience] = useState([]);
  const [education, setEducation] = useState([]);
  const [skills, setSkills] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const results = await Promise.all([
          axios.get(`${API}/projects`),
          axios.get(`${API}/experience`),
          axios.get(`${API}/education`),
          axios.get(`${API}/skills`),
          axios.get(`${API}/certifications`)
        ]);
        setProjects(results[0].data);
        setExperience(results[1].data);
        setEducation(results[2].data);
        setSkills(results[3].data);
        setCertifications(results[4].data.certifications || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="section" style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div className="loading-spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  // Get featured project (first one) and remaining projects
  const featuredProject = projects[0];
  const otherProjects = projects.slice(1);

  return (
    <div className="ux-portfolio" data-testid="ux-portfolio">
      {/* Featured Project Section */}
      {featuredProject && <FeaturedProject project={featuredProject} />}

      {/* Other Projects Section */}
      <section id="work" className="section">
        <div className="section-header">
          <span className="section-label">More Work</span>
          <h2 className="section-title">Additional Projects</h2>
          <p className="subtitle">
            Real-world research driving evidence-based design decisions
          </p>
        </div>

        <div className="projects-grid" data-testid="projects-grid">
          {otherProjects.map((proj) => (
            <ProjectCard key={proj.id} project={proj} />
          ))}
        </div>
      </section>

      {/* Skills Section */}
      <div className="section-alt">
        <section id="skills" className="section">
          <div className="section-header">
            <span className="section-label">Expertise</span>
            <h2 className="section-title">Research Skills</h2>
          </div>

          <div className="skills-container" data-testid="skills-container">
            {skills.map((s) => (
              <SkillCard key={s.id} skillCat={s} />
            ))}
          </div>
        </section>
      </div>

      {/* Experience Section */}
      <section id="about" className="section">
        <div className="experience-grid">
          <div>
            <div className="section-header" style={{ textAlign: 'left', marginBottom: '40px' }}>
              <span className="section-label">Career</span>
              <h2 className="section-title">Experience</h2>
            </div>
            
            <div className="timeline" data-testid="experience-timeline">
              {experience.map((exp) => (
                <TimelineItem key={exp.id} exp={exp} />
              ))}
            </div>
          </div>

          <div>
            <div className="section-header" style={{ textAlign: 'left', marginBottom: '40px' }}>
              <span className="section-label">Academic</span>
              <h2 className="section-title">Education</h2>
            </div>
            
            <div data-testid="education-list">
              {education.map((edu) => (
                <EducationCard key={edu.id} edu={edu} />
              ))}
            </div>

            <div className="skill-card" style={{ marginTop: '20px' }}>
              <h4 className="skill-title">Certifications</h4>
              <div className="skill-list" data-testid="certifications-list">
                {certifications.map((cert, idx) => (
                  <span key={idx} className="skill-tag">{cert}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UXPortfolio;
